import axios from 'axios';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';
import { getAuth } from '../providers/AppAuthProvider';
import { Configs } from '../resources/whitelist/configs/useConfigs';
import { Referral, RewardTier, Tier } from '../resources/whitelist/reward/List';

export async function retrieveConfigs() {
  const url = `${baseUrl}/leverage-prediction/whitelist/configs`;

  const { accessToken } = (await getAuth()) || {};

  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });

  return res.data?.data ?? res.data;
}

export async function updateConfigs(candidate: Configs) {
  const url = `${baseUrl}/leverage-prediction/whitelist/configs`;

  const { accessToken } = (await getAuth()) || {};
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };

  return axios.put(
    url,
    {
      startAt: candidate.startAt,
      endAt: candidate.endAt,
      earlyAccessAt: candidate.earlyAccessAt,
      baseCount: candidate.baseCount,
      growthIntervalMs: candidate.growthIntervalMs,
      growthMin: candidate.growthMin,
      growthMax: candidate.growthMax,
      earlyBirdPoolTotalUsdc: candidate.earlyBirdPoolTotalUsdc,
      earlyBirdPerUserUsdc: candidate.earlyBirdPerUserUsdc,
      earlyBirdMinVolumeUsdc: candidate.earlyBirdMinVolumeUsdc,
      earlyBirdTopN: candidate.earlyBirdTopN,
      unlockVolumeUsdc: candidate.unlockVolumeUsdc,
      referralMinTradeUsdc: candidate.referralMinTradeUsdc,
      marketingShowcaseQuota: candidate.marketingShowcaseQuota,
    },
    { headers: headers }
  );
}

export async function resetConfigs() {
  const url = `${baseUrl}/leverage-prediction/whitelist/configs/reset-shortlist`;

  const { accessToken } = (await getAuth()) || {};
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };

  return axios.post(url, null, { headers: headers });
}

export async function retreiveEarlyBird(pageNumber?: number, pageSize?: number) {
  const url = `${baseUrl}/leverage-prediction/whitelist/early-bird?page=${pageNumber}&pageSize=${pageSize}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function retreiveEarlyBirdSummary() {
  const url = `${baseUrl}/leverage-prediction/whitelist/early-bird/summary`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function retrieveLeaderboard(limit: number) {
  const url = `${baseUrl}/leverage-prediction/whitelist/leaderboard?limit=${limit}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function retrieveLeaderboardSummary() {
  const url = `${baseUrl}/leverage-prediction/whitelist/leaderboard/summary`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function issuedLeaderboard(ids: string[]) {
  const url = `${baseUrl}/leverage-prediction/whitelist/leaderboard/distribute`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(
    url,
    { userIds: ids },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function issuedInvite(referrerIds: string[]) {
  const url = `${baseUrl}/leverage-prediction/whitelist/invite/distribute`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(
    url,
    { referrerIds },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function retrieveInvite(
  pageNumber?: number,
  pageSize?: number,
  search?: string,
  validOnly?: boolean,
  notDistributed?: boolean
) {
  const params = new URLSearchParams();
  if (pageNumber != null) params.append('page', String(pageNumber));
  if (pageSize != null) params.append('pageSize', String(pageSize));
  if (search) params.append('search', search);
  if (validOnly != null) params.append('validOnly', String(validOnly));
  if (notDistributed != null) params.append('notDistributed', String(notDistributed));

  const url = `${baseUrl}/leverage-prediction/whitelist/invite?${params.toString()}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function retrieveInviteSummary() {
  const url = `${baseUrl}/leverage-prediction/whitelist/invite/summary`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function retrieveRedeem(pageNumber?: number, pageSize?: number) {
  const url = `${baseUrl}/leverage-prediction/whitelist/redeem?page=${pageNumber}&pageSize=${pageSize}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function generateRedeemCode(code: string, notes: string, maxUses: number) {
  const url = `${baseUrl}/leverage-prediction/whitelist/redeem`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(
    url,
    { code: code, notes: notes, maxUses: maxUses },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function retrieveRedeemSummary() {
  const url = `${baseUrl}/leverage-prediction/whitelist/redeem/summary`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function deleteRedeem(id: string) {
  const url = `${baseUrl}/leverage-prediction/whitelist/redeem/${id}/disable`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(url, null, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function retrieveFAQ() {
  const url = `${baseUrl}/leverage-prediction/whitelist/faq`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function deleteFAQById(id: string) {
  const url = `${baseUrl}/leverage-prediction/whitelist/faq/${id}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.delete(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function createFAQ(
  questionEn: string,
  questionVi: string,
  questionHi: string,
  questionZh: string,
  questionHk: string,
  answerEn: string,
  answerVi: string,
  answerHi: string,
  answerZh: string,
  answerHk: string
) {
  const url = `${baseUrl}/leverage-prediction/whitelist/faq`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(
    url,
    {
      questionEn,
      questionVi,
      questionHi,
      questionZh,
      questionHk,
      answerEn,
      answerVi,
      answerHi,
      answerZh,
      answerHk,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function orderFAQ(orderedIds: string[]) {
  const url = `${baseUrl}/leverage-prediction/whitelist/faq/reorder`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(
    url,
    { orderedIds },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function updateFAQById(
  id: string,
  questionEn: string,
  questionVi: string,
  questionHi: string,
  questionZh: string,
  questionHk: string,
  answerEn: string,
  answerVi: string,
  answerHi: string,
  answerZh: string,
  answerHk: string
) {
  const url = `${baseUrl}/leverage-prediction/whitelist/faq/${id}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.put(
    url,
    {
      questionEn,
      questionVi,
      questionHi,
      questionZh,
      questionHk,
      answerEn,
      answerVi,
      answerHi,
      answerZh,
      answerHk,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function retrieveRewardTicket() {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/ticket`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function saveRewardTicket(tiers: Tier[]) {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/ticket`;

  const { accessToken } = (await getAuth()) || {};

  return axios.put(
    url,
    { tiers },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function retrieveRewardLeaderboard() {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/leaderboard`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function saveRewardLeaderboard(tiers: RewardTier[]) {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/leaderboard`;

  const { accessToken } = (await getAuth()) || {};

  return axios.put(
    url,
    { tiers },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function retrieveRewardReferral() {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/referral`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function saveRewardReferral(tiers: Referral[]) {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/referral`;

  const { accessToken } = (await getAuth()) || {};

  return axios.put(
    url,
    { tiers },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function retrieveManually(pageNumber?: number, pageSize?: number) {
  const url = `${baseUrl}/leverage-prediction/whitelist/manually?page=${pageNumber}&pageSize=${pageSize}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
}

export async function addManually(userId: string, walletAddress: string, notes: string, proxyWalletAddress?: string) {
  const url = `${baseUrl}/leverage-prediction/whitelist/manually`;

  const { accessToken } = (await getAuth()) || {};

  return axios.post(
    url,
    { userId: userId || undefined, walletAddress: walletAddress || undefined, proxyWalletAddress: proxyWalletAddress || undefined, notes },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function deleteManually(
  userId: string,
  walletAddress: string,
  proxyWalletAddress: string
) {
  const url = `${baseUrl}/leverage-prediction/whitelist/manually`;

  const { accessToken } = (await getAuth()) || {};

  return axios.delete(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
    data: {
      userId,
      walletAddress,
      proxyWalletAddress,
    },
  });
}

export async function updateWhitelist(unlockVolumeUsdc: string) {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/eligibility`;

  const { accessToken } = (await getAuth()) || {};

  return axios.patch(
    url,
    { unlockVolumeUsdc },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function updateEarlyBird(
  earlyBirdPoolTotalUsdc: string,
  earlyBirdPerUserUsdc: string,
  earlyBirdMinVolumeUsdc: string,
  earlyBirdTopN: string
) {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/early-bird`;

  const { accessToken } = (await getAuth()) || {};

  return axios.patch(
    url,
    { earlyBirdPoolTotalUsdc, earlyBirdPerUserUsdc, earlyBirdMinVolumeUsdc, earlyBirdTopN },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
      },
    }
  );
}

export async function downloadEarlyBirdCSV() {
  const url = `${baseUrl}/leverage-prediction/whitelist/early-bird/csv`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
  });
}

export async function downloadLeaderboardCSV() {
  const url = `${baseUrl}/leverage-prediction/whitelist/leaderboard/csv?limit=500`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
  });
}

export async function downloadInviteDataCSV(
  search?: string,
  validOnly?: boolean,
  notDistributed?: boolean
) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (validOnly != null) params.append('validOnly', String(validOnly));
  if (notDistributed != null) params.append('notDistributed', String(notDistributed));

  const url = `${baseUrl}/leverage-prediction/whitelist/invite/csv?${params.toString()}`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
  });
}

export async function downloadRedeemCSV() {
  const url = `${baseUrl}/leverage-prediction/whitelist/redeem/csv`;

  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: '*/*',
    },
  });
}

export async function resetReward() {
  const url = `${baseUrl}/leverage-prediction/whitelist/reward-config/restore`;

  const { accessToken } = (await getAuth()) || {};
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };

  return axios.post(url, null, { headers: headers });
}

export async function retrieveEventParticipants(
  page: number,
  pageSize: number,
  search?: string,
  statuses?: string[],
  orderField?: string,
  orderDirection?: string
) {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search) params.set('search', search);
  if (statuses && statuses.length > 0) {
    statuses.forEach((s) => params.append('statuses', s));
  }
  if (orderField) params.set('orderField', orderField);
  if (orderDirection) params.set('orderDirection', orderDirection);

  const url = `${baseUrl}/leverage-prediction/whitelist/event-participants?${params.toString()}`;
  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
  });
}

export async function downloadEventParticipantsCSV(
  search?: string,
  redemptionCode?: string,
  orderField?: string,
  orderDirection?: string
) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (redemptionCode) params.set('redemptionCode', redemptionCode);
  if (orderField) params.set('orderField', orderField);
  if (orderDirection) params.set('orderDirection', orderDirection);
  const query = params.toString();

  const url = `${baseUrl}/leverage-prediction/whitelist/event-participants/csv${query ? `?${query}` : ''}`;
  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    responseType: 'blob',
    headers: { Authorization: `Bearer ${accessToken}`, Accept: '*/*' },
  });
}

export async function retrieveWhitelistedUsers(
  page: number,
  pageSize: number,
  search?: string,
  unlockSource?: string,
  redemptionCode?: string
) {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search) params.set('search', search);
  if (unlockSource) params.set('unlockSource', unlockSource);
  if (redemptionCode) params.set('redemptionCode', redemptionCode);

  const url = `${baseUrl}/leverage-prediction/whitelist/whitelisted-users?${params.toString()}`;
  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
  });
}

export async function downloadWhitelistedUsersCSV(search?: string, unlockSource?: string, redemptionCode?: string) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (unlockSource) params.set('unlockSource', unlockSource);
  if (redemptionCode) params.set('redemptionCode', redemptionCode);
  const query = params.toString();

  const url = `${baseUrl}/leverage-prediction/whitelist/whitelisted-users/csv${query ? `?${query}` : ''}`;
  const { accessToken } = (await getAuth()) || {};

  return axios.get(url, {
    responseType: 'blob',
    headers: { Authorization: `Bearer ${accessToken}`, Accept: '*/*' },
  });
}

export async function retrieveReferralPool() {
  const url = `${baseUrl}/leverage-prediction/whitelist/referral-pool`;
  const { accessToken } = (await getAuth()) || {};
  return axios.get(url, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
  });
}

export async function updateReferralPool(totalUsdc: string) {
  const url = `${baseUrl}/leverage-prediction/whitelist/referral-pool`;
  const { accessToken } = (await getAuth()) || {};
  return axios.put(
    url,
    { totalUsdc },
    { headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' } }
  );
}

export async function resetFaq() {
  const url = `${baseUrl}/leverage-prediction/whitelist/faq/reset`;

  const { accessToken } = (await getAuth()) || {};
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };

  return axios.post(url, null, { headers: headers });
}